import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { ChevronRight } from "lucide-react";
import { Badge, type StatusType } from "@mebi/ui";

const cardVariants = cva("", {
  variants: {
    variant: {
      home: "shadow-sm shadow-neutral-200/60",
      organizer: "shadow-sm shadow-neutral-200/60",
    },
  },
  defaultVariants: {
    variant: "home",
  },
});

export interface CompetitionCardProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof cardVariants> {
  /** Title of the category */
  title: string;
  /** Current number of registrations */
  registrationCount: number;
  /** Optional player limit per category */
  playerLimit?: number;
  /** Raw status value (e.g., "open", "registration_open", "ongoing", "Đang diễn ra") */
  statusValue?: string;
  /** Status type to resolve mapping from shared status module, defaults to 'category' */
  statusType?: StatusType;
  /** Backward compatibility: explicit status label */
  statusLabel?: string;
  /** Click handler for the whole card */
  onClick?: () => void;
}

/**
 * CompetitionCard - Competition/Card component
 * Displays: title, registration ratio, status chip and a chevron icon.
 * Designed to be reusable in player and organizer flows.
 */
const CompetitionCard = React.forwardRef<HTMLButtonElement, CompetitionCardProps>(
  (
    {
      className,
      variant,
      title,
      registrationCount,
      playerLimit,
      statusType = "category",
      statusValue,
      statusLabel,
      onClick,
      ...props
    },
    ref,
  ) => {
    const ratio = (playerLimit && playerLimit > 0) ? (registrationCount / playerLimit) : 0;
    
    let countTextClass = "text-Color-Text-semantic-positive"; // default: < 75%
    if (ratio >= 1) {
      countTextClass = "text-Color-Text-semantic-negative"; // Đã hết slot
    } else if (ratio >= 0.75) {
      // Sắp hết slot
      countTextClass = "text-Color-Text-brand-default"; 
    }

    return (
      <button
        type="button"
        ref={ref}
        className={cn(
          cardVariants({ variant }),
          "cursor-pointer select-none rounded-2xl",
          "bg-Color-Background-neutral-default_primary",
          "ring-1 ring-inset ring-Color-Stroke-neutral",
          "hover:bg-Color-Background-neutral-hover hover:ring-Color-Stroke-neutral",
          "active:bg-Color-Background-neutral-pressed active:ring-Color-Stroke-neutral",
          "focus:ring-2 focus:ring-inset focus:ring-Color-Stroke-focus focus:bg-Color-Background-neutral-focus outline-none",
          "transition-all duration-200 px-4 py-2",
          "w-full text-left inline-flex justify-between items-center",
          className,
        )}
        onClick={onClick}
        {...props}
      >
        {/* Left: Info section */}
        <div className="inline-flex flex-col items-start min-w-0">
          <div className="text-center justify-center text-Color-Text-neutral-black text-base font-semibold font-['Be_Vietnam_Pro'] leading-6 truncate max-w-[280px]">
            {title}
          </div>

          <div className="inline-flex justify-start items-start gap-2 mt-1">
            <div className="flex justify-start items-center gap-1">
              <span className={cn(countTextClass, "text-center justify-center text-[10px] font-light font-['Be_Vietnam_Pro'] leading-4")}>
                {registrationCount ?? 0}
              </span>
              <span className={cn(countTextClass, "text-center justify-center text-[10px] font-light font-['Be_Vietnam_Pro'] leading-4")}>
                /
              </span>
              <span className={cn(countTextClass, "text-[10px] font-light font-['Be_Vietnam_Pro'] leading-4 text-center justify-center")}>
                {playerLimit}
              </span>
              <span className={cn(countTextClass, "text-[10px] font-light font-['Be_Vietnam_Pro'] leading-4 text-center justify-center")}>
                người đăng ký
              </span>
            </div>

            {statusValue || statusLabel ? (
              <div className="flex justify-start items-start">
                <Badge
                  statusType={statusType}
                  statusValue={statusValue}
                  size="sm"
                >
                  {statusLabel}
                </Badge>
              </div>
            ) : null}
          </div>
        </div>

        {/* Right: Chevron */}
        <div className="w-6 h-6 relative overflow-hidden origin-top-left flex items-center justify-center">
            <ChevronRight className="w-5 h-5 text-Color-Icon-neutral" />
        </div>
      </button>
    );
  },
);

CompetitionCard.displayName = "CompetitionCard";

export { CompetitionCard };
